<template>
  <div class="min-h-full">
    <div class="bg-blue-600 pb-32">
      <Disclosure
        v-slot="{ open, close }"
        as="nav"
        class="border-b border-blue-300 border-opacity-25 bg-blue-600 lg:border-none"
      >
        <div class="mx-auto max-w-7xl px-2 sm:px-4 lg:px-8">
          <div
            class="relative flex h-16 items-center justify-between lg:border-b lg:border-blue-400 lg:border-opacity-25"
          >
            <!-- Rest of the header content remains the same -->
            <div class="flex items-center px-2 lg:px-0">
              <div class="flex flex-row">
                <div
                  class="flex items-center justify-center bg-white rounded-full p-0.5 mr-5"
                >
                  <img
                    alt=""
                    class="block h-8 w-8 rounded-full"
                    src="/favicon.ico"
                  />
                </div>
                <div class="flex items-center">
                  <div class="text-2xl font-bold text-white">ShuleHub</div>
                </div>
              </div>
              <div class="hidden lg:ml-10 lg:block">
                <div class="flex space-x-4">
                  <NuxtLink
                    v-for="item in navigation"
                    :key="item.name"
                    :class="[
                      isCurrentRoute(item.href)
                        ? 'bg-blue-700 text-white'
                        : 'text-white hover:bg-blue-500 hover:bg-opacity-75',
                      'rounded-md px-3 py-2 text-sm font-medium',
                    ]"
                    :to="item.href"
                    >{{ item.name }}
                  </NuxtLink>
                </div>
              </div>
            </div>

            <div class="flex lg:hidden">
              <DisclosureButton
                class="relative inline-flex items-center justify-center rounded-md bg-blue-600 p-2 text-blue-200 hover:bg-blue-500 hover:bg-opacity-75 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600"
              >
                <span class="absolute -inset-0.5" />
                <span class="sr-only">Open main menu</span>
                <Bars3Icon
                  v-if="!open"
                  aria-hidden="true"
                  class="block h-6 w-6"
                />
                <XMarkIcon v-else aria-hidden="true" class="block h-6 w-6" />
              </DisclosureButton>
            </div>
            <div class="hidden lg:ml-4 lg:block">
              <div class="flex items-center">
                <div v-if="user" class="flex items-center">
                  <MessagesIcon class="mr-4" />
                  <button
                    class="relative flex-shrink-0 rounded-full bg-blue-600 p-1 text-blue-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600"
                    type="button"
                  >
                    <span class="absolute -inset-1.5" />
                    <span class="sr-only">View notifications</span>
                    <!--                    <BellIcon aria-hidden="true" class="h-6 w-6"/>-->
                  </button>

                  <Menu as="div" class="relative ml-3 flex-shrink-0">
                    <div>
                      <MenuButton
                        class="relative flex rounded-full bg-blue-600 text-sm text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600"
                      >
                        <span class="absolute -inset-1.5" />
                        <span class="sr-only">Open user menu</span>
                        <img
                          v-if="user.user_metadata.avatar_url"
                          :src="user.user_metadata.avatar_url"
                          alt=""
                          class="h-8 w-8 rounded-full"
                        />
                      </MenuButton>
                    </div>

                    <transition
                      enter-active-class="transition ease-out duration-100"
                      enter-from-class="transform opacity-0 scale-95"
                      enter-to-class="transform opacity-100 scale-100"
                      leave-active-class="transition ease-in duration-75"
                      leave-from-class="transform opacity-100 scale-100"
                      leave-to-class="transform opacity-0 scale-95"
                    >
                      <MenuItems
                        class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                      >
                        <MenuItem
                          v-for="item in userNavigation"
                          :key="item.name"
                          v-slot="{ active }"
                        >
                          <NuxtLink
                            v-if="item.href !== '#'"
                            :class="[
                              active ? 'bg-gray-100' : '',
                              'block px-4 py-2 text-sm text-gray-700',
                            ]"
                            :to="item.href"
                            >{{ item.name }}
                          </NuxtLink>
                          <button
                            v-else
                            :class="[
                              active ? 'bg-gray-100' : '',
                              'block w-full text-left px-4 py-2 text-sm text-gray-700',
                            ]"
                            :disabled="loading"
                            @click="handleLogout"
                          >
                            <span v-if="loading">Signing out...</span>
                            <span v-else>Sign out</span>
                          </button>
                        </MenuItem>
                      </MenuItems>
                    </transition>
                  </Menu>
                </div>
                <GoogleLogin v-else />
              </div>
            </div>
          </div>
        </div>

        <DisclosurePanel class="lg:hidden">
          <div class="space-y-1 px-2 pb-3 pt-2">
            <!-- Updated mobile navigation links -->
            <DisclosureButton
              v-for="item in navigation"
              :key="item.name"
              :class="[
                isCurrentRoute(item.href)
                  ? 'bg-blue-700 text-white'
                  : 'text-white hover:bg-blue-500 hover:bg-opacity-75',
                'block rounded-md px-3 py-2 text-base font-medium',
              ]"
              as="div"
            >
              <NuxtLink :to="item.href" class="block w-full" @click="close">
                {{ item.name }}
              </NuxtLink>
            </DisclosureButton>
          </div>

          <div class="border-t border-blue-700 pb-3 pt-4">
            <div v-if="user" class="flex items-center px-5">
              <!-- User info section remains the same -->
            </div>
            <div class="mt-3 space-y-1 px-2">
              <div v-if="user">
                <!-- Updated user navigation links -->
                <DisclosureButton
                  v-for="item in userNavigation.filter(
                    (item) => item.href !== '#'
                  )"
                  :key="item.name"
                  as="div"
                  class="block rounded-md px-3 py-2 text-base font-medium text-white hover:bg-blue-500 hover:bg-opacity-75"
                >
                  <NuxtLink :to="item.href" class="block w-full" @click="close">
                    {{ item.name }}
                  </NuxtLink>
                </DisclosureButton>

                <button
                  :disabled="loading"
                  class="block w-full text-left rounded-md px-3 py-2 text-base font-medium text-white hover:bg-blue-500 hover:bg-opacity-75"
                  @click="
                    () => {
                      handleLogout();
                      close();
                    }
                  "
                >
                  <span v-if="loading">Signing out...</span>
                  <span v-else>Sign out</span>
                </button>
              </div>
              <GoogleLogin v-else />
            </div>
          </div>
        </DisclosurePanel>
      </Disclosure>
    </div>

    <main class="-mt-32">
      <div class="mx-auto max-w-7xl px-0 pb-12 sm:px-6 lg:px-8">
        <div class="rounded-lg bg-white px-0 py-6 sm:px-6">
          <!-- <SetLocationRequest class="mb-3"/> -->

          <Navigation v-if="user && user.email == 'tim.kariuki@gmail.com'" />
          <Communication />
          <slot />
        </div>
      </div>
    </main>
    <ReferralComponent />
    <UserDetailsTracker />
  </div>
</template>

<script setup>
import Navigation from "~/components/Admin/Navigation.vue";
import Communication from "~/components/Courses/Communication.vue";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/vue";

import { Bars3Icon, BellIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import { useRoute, useRouter } from "vue-router";
import { ref } from "vue";
import SetLocationRequest from "~/components/SetLocationRequest.vue";
import MessagesIcon from "~/components/Chat/MessagesIcon.vue";

const admins = ref(["tim.kariuki@gmail.com"]);

const user = useSupabaseUser();
const supabase = useSupabaseClient();
const route = useRoute();
const router = useRouter();

const navigation = [
  { name: "KUCCPS Programs", href: "/programs" },
  { name: "KCSE Results", href: "/results/all" },
  { name: "Schools", href: "/schools" },
  { name: "KICD Approved Books", href: "/books" },
  { name: "Books Market Place", href: "/market-place" },
  // { name: "Recon", href: "/account/reconciler" },
  { name: "Teachers' Swaps", href: "/swaps" },

  // {name: "My Store", href: "/store"},
  // {name: "Make Money", href: "/my-market-place"},
];

const userNavigation = [
  { name: "My Account", href: "/account" },
  { name: "My Transactions", href: "/transactions" },
  { name: "Referrals", href: "/referrals" },
  { name: "My Location", href: "/my-location" },
  { name: "Admin", href: "/stats" },
  { name: "Sign out", href: "#" },
];

const isCurrentRoute = (path) => {
  return route.path === path;
};

const loading = ref(false);

const handleLogout = async () => {
  loading.value = true;
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    await router.push("/");
  } catch (error) {
    console.error("Error during logout:", error);
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  if (!user.value) return;
  const { data: pubUserData, error: pubUserError } = await supabase
    .from("users")
    .select("store")
    .eq("id", user.value.id)
    .maybeSingle();
  if (!pubUserData) {
    router.push("/auth/callback");
  }
});
</script>
